import {Helmet} from '@dr.pogodin/react-helmet';
import {Box, getSize, Grid, GridItem, Show, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {ReactNode} from 'react';

import {isNotNil} from 'ramda';

import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {useSourcingPageHeaderHeight} from '../../hooks/useSourcingPageHeaderHeight';
import {isUnifiedSourcingEnabled} from '../../utils/isUnifiedSourcingEnabled';
import {ListingPreviewSkeleton} from '../ListingPreview/components/ListingPreviewSkeleton';
import {ResizablePreviewLayout} from './components/ResizablePreviewLayout';

type SourcingLayoutProps = {
  header: ReactNode;
  content: ReactNode;
  iframe?: ReactNode;
  hasPreview?: boolean;
  isPreviewLoading?: boolean;
  pageTitle?: string;
  noResultsContent?: ReactNode;
};

export function SourcingLayout(props: SourcingLayoutProps) {
  const documentTitle = props.pageTitle || i18n.t('page.sourcing.labels.sourcing');
  const pageHeaderHeight = useSourcingPageHeaderHeight();

  const preview = match({
    unified: isUnifiedSourcingEnabled(),
    hasPreview: props.hasPreview,
    loading: props.isPreviewLoading,
  })
    .with({unified: true, hasPreview: false}, () => null)
    .with({unified: true, loading: true}, () => <ListingPreviewSkeleton />)
    .otherwise(() => props.iframe);

  /* Same height as the container below so the padding is free to be padding. The explicit height
     is needed: a grid item and the resizable layout's main panel both size to their content. */
  const mainContent = (
    <Box height={`calc(100dvh - ${getSize(pageHeaderHeight)})`} padding={4}>
      <VStack spacing={4} height="100%">
        <Box flexShrink={0}>{props.header}</Box>
        {props.content}
      </VStack>
    </Box>
  );

  return (
    <>
      <Helmet title={documentTitle} />
      <Box height={`calc(100dvh - ${getSize(pageHeaderHeight)})`} overflow="hidden">
        {props.noResultsContent ?? (
          <>
            <Show whenFeatureEnabled={featureFlags.SOURCING_RESIZABLE_PREVIEW}>
              <ResizablePreviewLayout preview={preview} data-testid="sourcingLayout">
                {mainContent}
              </ResizablePreviewLayout>
            </Show>
            <Show whenFeatureDisabled={featureFlags.SOURCING_RESIZABLE_PREVIEW}>
              <Grid columns={[1, 1, 1, isNotNil(preview) ? 3 : 2]} spacing={0} minHeight="100%">
                <GridItem span={[1, 1, 1, 2]}>{mainContent}</GridItem>
                <Show onDesktop when={isNotNil(preview)}>
                  {preview}
                </Show>
              </Grid>
            </Show>
          </>
        )}
      </Box>
    </>
  );
}
